import { getCadProjectId } from './cadAccess';
import { isCadMapDisplay } from './cadGeometry';

/** Los que acepta `GET /getcadcanvaspresignedurl`. */
export const CAD_CANVAS_IMAGE_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * Ilustración del lienzo (`GET /getcadproject`). Sin url usable → null.
 * Un GET viejo que omita `canvas_image` no rompe el plano.
 *
 * @param {object|null|undefined} project
 * @returns {{ url: string, key: string|null }|null}
 */
export function readCadCanvasImage(project) {
  const image = project?.canvas_image;
  if (!image || typeof image !== 'object') return null;
  const url = typeof image.url === 'string' ? image.url.trim() : '';
  if (!url) return null;
  const key = typeof image.key === 'string' ? image.key.trim() : '';
  return { url, key: key || null };
}

/**
 * Quad WGS84 del GET (`canvas_image.map_coordinates`): TL, TR, BR, BL.
 * Fallback del overlay. **No** es la fuente de verdad: el GET hornea un
 * contain distinto al del panel y, si se pinta tal cual al Guardar, el PNG
 * salta. Ver `resolveCadMapCanvasImageCoordinates`.
 *
 * @param {object|null|undefined} project
 * @returns {[[number, number], [number, number], [number, number], [number, number]]|null}
 */
export function readCadCanvasImageMapCoordinates(project) {
  const raw = project?.canvas_image?.map_coordinates;
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const corners = [];
  for (let i = 0; i < 4; i += 1) {
    const lng = Number(raw[i]?.[0]);
    const lat = Number(raw[i]?.[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    corners.push([lng, lat]);
  }
  return corners;
}

/**
 * Esquinas del PNG en el mapa. Una sola regla, dueño / visitante / incógnito /
 * Referenciar:
 *
 * **Si hay encaje vivo, se pinta el vivo.** `map_coordinates` solo si no hay.
 *
 * No preferir el GET cuando `getHasTransform` es true: ese quad no coincide
 * con contain + `scaleX`/`scaleY` + georef del panel, y Guardar mueve el PNG.
 *
 * @param {{
 *   mapCoordinates?: number[][]|null,
 *   liveCoordinates?: number[][]|null,
 * }} input
 * @returns {number[][]|null}
 */
export function resolveCadMapCanvasImageCoordinates({
  mapCoordinates = null,
  liveCoordinates = null,
} = {}) {
  if (liveCoordinates) return liveCoordinates;
  return mapCoordinates || null;
}

/**
 * El GET trajo un encaje distinto del contain. Entonces
 * `map_coordinates` ya debería incluirlo.
 *
 * @param {object|null|undefined} project
 * @returns {boolean}
 */
export function cadCanvasImageGetHasTransform(project) {
  const fromGet = readCadCanvasImageTransformFromProject(project);
  return Boolean(fromGet) && isCadCanvasImageTransformDirty(fromGet);
}

export function hasCadCanvasImage(project) {
  return Boolean(readCadCanvasImage(project));
}

/**
 * ¿Pintar el PNG? Independiente de que el archivo esté guardado.
 * GET `show_plan_image`. Si falta y hay imagen → true (listas viejas).
 * Sin imagen → false: no hay nada que mostrar.
 *
 * @param {object|null|undefined} project
 * @returns {boolean}
 */
export function readCadShowPlanImage(project) {
  if (!hasCadCanvasImage(project)) return false;
  const raw = project?.show_plan_image;
  if (raw === false || raw === 'false' || raw === 0) return false;
  if (raw === true || raw === 'true' || raw === 1) return true;
  return true;
}

/**
 * Calles / plaza / árboles. XOR con el PNG: no se apilan.
 * En align (encaje de la imagen) se ocultan aunque el switch esté apagado.
 *
 * @param {object|null|undefined} project
 * @param {{ alignMode?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldPaintCadMasterplan(project, { alignMode = false } = {}) {
  if (alignMode) return false;
  return !readCadShowPlanImage(project);
}

/**
 * Vista que hay que pintar. Georef y PNG son independientes:
 * mapa si el GET es `map`; si no, ilustración si hay archivo; si no, lienzo.
 * `image` sin archivo se trata como `canvas`.
 *
 * @param {object|null|undefined} project
 * @returns {'map'|'canvas'|'image'}
 */
export function getEffectiveCadDisplayMode(project) {
  if (isCadMapDisplay(project)) return 'map';
  if (hasCadCanvasImage(project)) return 'image';
  return 'canvas';
}

/** PNG debajo de los lotes en el lienzo (sin mapa). Respeta `show_plan_image`. */
export function shouldPaintCadPlanImage(project) {
  return getEffectiveCadDisplayMode(project) === 'image' && readCadShowPlanImage(project);
}

/**
 * En `map`, el mismo PNG puede ir debajo del CAD con el georef del plano.
 * No sustituye la vista `image` (ahí no hay Mapbox). XOR con el masterplan.
 */
export function shouldPaintCadMapImage(project) {
  return isCadMapDisplay(project) && readCadShowPlanImage(project);
}

export const CAD_CANVAS_IMAGE_TRANSFORM_RESET = {
  dx: 0,
  dy: 0,
  scale: 1,
  scaleX: 1,
  scaleY: 1,
  rotationRad: 0,
};

/** Holgura alrededor del CAD: 1× el lado largo a cada lado. No kilómetros. */
export const CAD_CANVAS_IMAGE_TRAVEL_PAD_RATIO = 1;
export const CAD_CANVAS_IMAGE_SCALE_MIN = 0.25;
export const CAD_CANVAS_IMAGE_SCALE_MAX = 4;

function isFiniteBounds(bounds) {
  const minX = Number(bounds?.minX);
  const minY = Number(bounds?.minY);
  const maxX = Number(bounds?.maxX);
  const maxY = Number(bounds?.maxY);
  return [minX, minY, maxX, maxY].every(Number.isFinite) && maxX > minX && maxY > minY
    ? { minX, minY, maxX, maxY }
    : null;
}

/**
 * Recuadro amplio en metros DXF para no perder el PNG.
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @param {number} [padRatio]
 */
export function expandCadCanvasImageTravelBounds(
  bounds,
  padRatio = CAD_CANVAS_IMAGE_TRAVEL_PAD_RATIO,
) {
  const box = isFiniteBounds(bounds);
  if (!box) return null;
  const pad = Math.max(box.maxX - box.minX, box.maxY - box.minY) * Number(padRatio || 0);
  if (!(pad >= 0)) return box;
  return {
    minX: box.minX - pad,
    minY: box.minY - pad,
    maxX: box.maxX + pad,
    maxY: box.maxY + pad,
  };
}

function clampCadCanvasImageScale(value) {
  return Math.min(
    CAD_CANVAS_IMAGE_SCALE_MAX,
    Math.max(CAD_CANVAS_IMAGE_SCALE_MIN, value),
  );
}

/**
 * El centro del PNG queda dentro del recuadro amplio del CAD.
 * La escala no se va de los sliders (25%–400%).
 *
 * @param {object|null|undefined} transform
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} [bounds]
 * @param {{ width?: number, height?: number }|null} [imageSize]
 */
export function clampCadCanvasImageTransform(transform, bounds, imageSize) {
  const pose = normalizeCadCanvasImageTransform(transform);
  const scale = clampCadCanvasImageScale(pose.scale);
  const scaleX = clampCadCanvasImageScale(pose.scaleX);
  const scaleY = clampCadCanvasImageScale(pose.scaleY);
  const next = {
    ...pose,
    scale,
    scaleX,
    scaleY,
  };
  const travel = expandCadCanvasImageTravelBounds(bounds);
  const rect = containCadImageRect(bounds, imageSize?.width, imageSize?.height);
  if (!travel || !rect) return next;
  const cx = (rect.minX + rect.maxX) / 2 + next.dx;
  const cy = (rect.minY + rect.maxY) / 2 + next.dy;
  const clampedCx = Math.min(Math.max(cx, travel.minX), travel.maxX);
  const clampedCy = Math.min(Math.max(cy, travel.minY), travel.maxY);
  return {
    ...next,
    dx: next.dx + (clampedCx - cx),
    dy: next.dy + (clampedCy - cy),
  };
}

function readPositiveScale(value, fallback) {
  const next = Number(value);
  return Number.isFinite(next) && next > 0 ? next : fallback;
}

/**
 * Ajuste del render (metros DXF). GET `canvas_image.transform` o este browser.
 * `scale` es Tamaño (zoom). `scaleX` / `scaleY` son Ancho / Alto. Si el GET
 * no trae los ejes (pose vieja), copian `scale`. No pisar ejes con `scale`
 * cuando ya vinieron.
 * @param {object|null|undefined} transform
 */
export function normalizeCadCanvasImageTransform(transform) {
  const dx = Number(transform?.dx);
  const dy = Number(transform?.dy);
  const rotationRad = Number(transform?.rotationRad);
  const uniform = readPositiveScale(transform?.scale, 1);
  const scaleX = readPositiveScale(transform?.scaleX, uniform);
  const scaleY = readPositiveScale(transform?.scaleY, uniform);
  return {
    dx: Number.isFinite(dx) ? dx : 0,
    dy: Number.isFinite(dy) ? dy : 0,
    scale: uniform,
    scaleX,
    scaleY,
    rotationRad: Number.isFinite(rotationRad) ? rotationRad : 0,
  };
}

export function isCadCanvasImageTransformDirty(transform) {
  const next = normalizeCadCanvasImageTransform(transform);
  return next.dx !== 0
    || next.dy !== 0
    || next.scale !== 1
    || next.scaleX !== 1
    || next.scaleY !== 1
    || next.rotationRad !== 0;
}

const TRANSFORM_STORAGE_PREFIX = 'cad.canvasImageTransform.';

/**
 * Una ilustración por lista. La key es el id: no depende de url/key de S3
 * (el presign cambia y si no, al recargar se pierde el encaje).
 * @param {string} projectId
 */
export function cadCanvasImageTransformStorageKey(projectId) {
  const id = String(projectId || '').trim();
  if (!id) return '';
  return `${TRANSFORM_STORAGE_PREFIX}${id}`;
}

function readCadCanvasImageTransformFromProject(project) {
  const raw = project?.canvas_image?.transform || project?.canvas_image_transform;
  if (!raw || typeof raw !== 'object') return null;
  return normalizeCadCanvasImageTransform(raw);
}

function readCadCanvasImageTransformFromStorage(project) {
  const key = cadCanvasImageTransformStorageKey(getCadProjectId(project));
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return normalizeCadCanvasImageTransform(JSON.parse(raw));
  } catch (_) {
    return null;
  }
}

/**
 * Encaje: GET `canvas_image.transform` (scale + Ancho/Alto) o este browser.
 */
export function readCadCanvasImageTransform(project) {
  return readCadCanvasImageTransformFromProject(project)
    || readCadCanvasImageTransformFromStorage(project)
    || { ...CAD_CANVAS_IMAGE_TRANSFORM_RESET };
}

/**
 * Persiste el encaje. `RESET` borra: solo llamarlo a propósito
 * (restablecer / quitar / reemplazar), nunca en el mount.
 */
export function writeCadCanvasImageTransform(project, transform) {
  const key = cadCanvasImageTransformStorageKey(getCadProjectId(project));
  if (!key) return;
  const pose = normalizeCadCanvasImageTransform(transform);
  try {
    if (!isCadCanvasImageTransformDirty(pose)) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(pose));
  } catch (_) { /* quota / modo privado */ }
}

export function clearCadCanvasImageTransform(project) {
  writeCadCanvasImageTransform(project, CAD_CANVAS_IMAGE_TRANSFORM_RESET);
}

const SIZE_STORAGE_PREFIX = 'cad.canvasImageSize.';

function cadCanvasImageSizeStorageKey(projectId) {
  const id = String(projectId || '').trim();
  if (!id) return '';
  return `${SIZE_STORAGE_PREFIX}${id}`;
}

function normalizeCadCanvasImageSize(size) {
  const width = Number(size?.width);
  const height = Number(size?.height);
  if (!(width > 0) || !(height > 0)) return null;
  return { width, height };
}

/** Aspect ya visto en este browser: el contain no espera el decode. */
export function readCadCanvasImageSizeHint(project) {
  const key = cadCanvasImageSizeStorageKey(getCadProjectId(project));
  if (!key) return null;
  try {
    return normalizeCadCanvasImageSize(JSON.parse(window.localStorage.getItem(key) || ''));
  } catch (_) {
    return null;
  }
}

export function writeCadCanvasImageSizeHint(project, size) {
  const key = cadCanvasImageSizeStorageKey(getCadProjectId(project));
  if (!key) return;
  const next = normalizeCadCanvasImageSize(size);
  try {
    if (!next) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch (_) { /* quota / modo privado */ }
}

export function clearCadCanvasImageSizeHint(project) {
  writeCadCanvasImageSizeHint(project, null);
}

/**
 * Tamaño del archivo local (subida / reemplazo) sin esperar S3.
 * @param {File|Blob|null|undefined} file
 * @returns {Promise<{ width: number, height: number }|null>}
 */
export function readCadImageFileSize(file) {
  if (!file) return Promise.resolve(null);
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const width = Number(image.naturalWidth);
      const height = Number(image.naturalHeight);
      URL.revokeObjectURL(objectUrl);
      resolve(width > 0 && height > 0 ? { width, height } : null);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    image.src = objectUrl;
  });
}

/**
 * Contain: el PNG entero cabe en `bounds` (letterbox si el aspect no calza).
 * Sin tamaño usable, se usa el recuadro completo (fill).
 *
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @param {number} [imageWidth]
 * @param {number} [imageHeight]
 */
export function containCadImageRect(bounds, imageWidth, imageHeight) {
  if (!bounds) return null;
  const boxW = Number(bounds.maxX) - Number(bounds.minX);
  const boxH = Number(bounds.maxY) - Number(bounds.minY);
  if (!(boxW > 0) || !(boxH > 0)) return null;
  const imgW = Number(imageWidth);
  const imgH = Number(imageHeight);
  if (!(imgW > 0) || !(imgH > 0)) {
    return {
      minX: Number(bounds.minX),
      minY: Number(bounds.minY),
      maxX: Number(bounds.maxX),
      maxY: Number(bounds.maxY),
    };
  }
  const imgAspect = imgW / imgH;
  const boxAspect = boxW / boxH;
  const width = imgAspect > boxAspect ? boxW : boxH * imgAspect;
  const height = imgAspect > boxAspect ? boxW / imgAspect : boxH;
  const cx = (Number(bounds.minX) + Number(bounds.maxX)) / 2;
  const cy = (Number(bounds.minY) + Number(bounds.maxY)) / 2;
  return {
    minX: cx - width / 2,
    maxX: cx + width / 2,
    minY: cy - height / 2,
    maxY: cy + height / 2,
  };
}

/**
 * Esquinas NW, NE, SE, SW en metros DXF: contain (letterbox con tamaño
 * natural) × `scaleX`/`scaleY` × rotación × `dx`/`dy`. Es la mitad del
 * overlay vivo; el sticker las pasa a WGS84. No sustituir por el quad
 * horneado del GET (`map_coordinates`).
 *
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }|null} bounds
 * @param {{ width?: number, height?: number }|null} [imageSize]
 * @param {object|null} [transform]
 * @returns {[[number, number], [number, number], [number, number], [number, number]]|null}
 */
export function cadCanvasImageMeterCorners(bounds, imageSize, transform) {
  const rect = containCadImageRect(bounds, imageSize?.width, imageSize?.height);
  if (!rect) return null;
  const pose = clampCadCanvasImageTransform(transform, bounds, imageSize);
  const cx = (rect.minX + rect.maxX) / 2 + pose.dx;
  const cy = (rect.minY + rect.maxY) / 2 + pose.dy;
  const hw = ((rect.maxX - rect.minX) / 2) * pose.scaleX;
  const hh = ((rect.maxY - rect.minY) / 2) * pose.scaleY;
  const cos = Math.cos(pose.rotationRad);
  const sin = Math.sin(pose.rotationRad);
  const corner = (x, y) => [cx + x * cos - y * sin, cy + x * sin + y * cos];
  return [
    corner(-hw, hh),
    corner(hw, hh),
    corner(hw, -hh),
    corner(-hw, -hh),
  ];
}

/**
 * Orden de capas del estilo. El raster del plano base va **debajo** del loteo:
 * si el loteo se repintó después (cambio de estilo), quedó arriba y lo tapa.
 *
 * @param {Array<{ id?: string }>|null|undefined} layers `map.getStyle().layers`
 * @param {string} layerId
 * @param {string} belowId
 * @returns {boolean}
 */
export function isMapLayerAbove(layers, layerId, belowId) {
  if (!Array.isArray(layers)) return false;
  const above = layers.findIndex((layer) => layer?.id === layerId);
  const below = layers.findIndex((layer) => layer?.id === belowId);
  return above > -1 && below > -1 && above > below;
}

export function isCadCanvasImageContentType(contentType) {
  return CAD_CANVAS_IMAGE_CONTENT_TYPES.includes(String(contentType || '').trim());
}

/**
 * MapLibre no recarga el bitmap si el source `image` sigue vivo y solo se
 * llama `updateImage` (https → data, o al revés). Hay que reemplazar el source.
 *
 * @param {string|null|undefined} lastUrl
 * @param {string|null|undefined} nextUrl
 * @returns {boolean}
 */
export function shouldReplaceCadCanvasImageSource(lastUrl, nextUrl) {
  const prev = String(lastUrl || '').trim();
  const next = String(nextUrl || '').trim();
  return Boolean(next) && prev !== next;
}

/** WebGL suele topear a 4096; un JPEG aéreo de 8k entra negro. */
export const CAD_CANVAS_IMAGE_MAP_MAX_EDGE = 2048;

const PREPARE_CACHE_LIMIT = 4;
const prepareCache = new Map();

/**
 * blob/data no llevan CORS. https de S3 sí: si un `Image()` previo cargó
 * sin `crossOrigin`, el caché HTTP queda sucio y el decode del mapa falla
 * a veces (incógnito / primera vez / reabrir).
 * @param {string} src
 */
export function isLocalCadCanvasImageUrl(src) {
  return src.startsWith('blob:') || src.startsWith('data:');
}

/**
 * Misma política CORS que el raster del mapa. El tamaño natural y el
 * canvas tienen que pegarle a S3 igual, o el segundo `Image()` pega error.
 * @param {HTMLImageElement} image
 * @param {string} src
 */
export function applyCadCanvasImageCors(image, src) {
  if (!isLocalCadCanvasImageUrl(src)) image.crossOrigin = 'anonymous';
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    applyCadCanvasImageCors(image, src);
    image.onload = () => {
      const width = Number(image.naturalWidth);
      const height = Number(image.naturalHeight);
      if (width > 0 && height > 0) resolve(image);
      else reject(new Error('No se pudo leer el plano base.'));
    };
    image.onerror = () => reject(new Error('No se pudo leer el plano base.'));
    image.src = src;
  });
}

/**
 * JPEG no tiene alfa: un PNG sin fondo se volvía negro en el mapa.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width
 * @param {number} height
 */
export function canvasHasTransparency(ctx, width, height) {
  if (!ctx || typeof ctx.getImageData !== 'function') return false;
  let data;
  try {
    data = ctx.getImageData(0, 0, width, height)?.data;
  } catch (_) {
    return false;
  }
  if (!data || data.length < 4) return false;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
}

/**
 * Bitmap origin-clean y acotado. MapLibre lo lee como source `canvas`
 * (sin un segundo fetch del blob: ese loadImage era el que fallaba la 1ª vez).
 *
 * @param {string} src
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function rasterizeCadCanvasImageForMap(src) {
  const image = await loadHtmlImage(src);
  const width = Number(image.naturalWidth);
  const height = Number(image.naturalHeight);
  const scale = Math.min(1, CAD_CANVAS_IMAGE_MAP_MAX_EDGE / Math.max(width, height));
  const nextW = Math.max(1, Math.round(width * scale));
  const nextH = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = nextW;
  canvas.height = nextH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo leer el plano base.');
  ctx.drawImage(image, 0, 0, nextW, nextH);
  return canvas;
}

/**
 * El source `canvas` a veces no copia píxeles si el elemento no está en el DOM.
 * CSS 1×1: WebGL usa width/height del bitmap, no el tamaño de pantalla.
 *
 * @param {import('maplibre-gl').Map|null} map
 * @param {HTMLCanvasElement|null|undefined} canvas
 */
export function attachCadCanvasImageElement(map, canvas) {
  if (!canvas || canvas.isConnected) return;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = [
    'position:absolute',
    'left:0',
    'top:0',
    'width:1px',
    'height:1px',
    'overflow:hidden',
    'pointer-events:none',
    'opacity:0',
  ].join(';');
  try { map?.getContainer?.()?.appendChild(canvas); } catch (_) { /* tests / mapa ido */ }
}

/**
 * @param {HTMLCanvasElement|null|undefined} canvas
 */
export function detachCadCanvasImageElement(canvas) {
  if (!canvas || !canvas.parentNode) return;
  try { canvas.parentNode.removeChild(canvas); } catch (_) { /* ya salió */ }
}

function waitCadCanvasImageMs(ms) {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

async function decodeCadCanvasImageForMap(src) {
  let lastError;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const canvas = await rasterizeCadCanvasImageForMap(src);
      return { from: src, canvas };
    } catch (error) {
      lastError = error;
      if (attempt < 2) await waitCadCanvasImageMs(400 * (attempt + 1));
    }
  }
  console.error('CAD: no se pudo rasterizar la imagen base', lastError);
  return null;
}

function rememberCadCanvasImagePrepare(src, pending) {
  if (prepareCache.has(src)) prepareCache.delete(src);
  prepareCache.set(src, pending);
  while (prepareCache.size > PREPARE_CACHE_LIMIT) {
    const oldest = prepareCache.keys().next().value;
    prepareCache.delete(oldest);
  }
}

/**
 * Canvas listo para el mapa. En tests se deja un `{ url }` sin decode.
 * Si S3 no alcanza, reintenta; no se cae a la URL cruda (MapLibre la volvía
 * a bajar y a veces perdía el blob).
 *
 * La misma URL no se decodifica dos veces: el overlay y el tamaño natural
 * arrancan el mismo promise apenas hay `canvas_image.url`.
 *
 * @param {string|null|undefined} url
 * @returns {Promise<{ from: string, canvas?: HTMLCanvasElement, url?: string }|null>}
 */
export function prepareCadCanvasImageForMap(url) {
  const src = typeof url === 'string' ? url.trim() : '';
  if (!src) return Promise.resolve(null);
  if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID) {
    return Promise.resolve({ from: src, url: src });
  }
  const cached = prepareCache.get(src);
  if (cached) return cached;
  const pending = decodeCadCanvasImageForMap(src).then((prepared) => {
    if (!prepared) prepareCache.delete(src);
    return prepared;
  });
  rememberCadCanvasImagePrepare(src, pending);
  return pending;
}
